import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import {
  NovedadHtmlBody,
  NovedadRichTextEditor,
} from "../Novedades/NovedadRichText";
import {
  htmlToPlainText,
  isEmptyNovedadHtml,
  sanitizeNovedadHtml,
} from "../../utils/novedadHtml";

function formatNoteTime(value, locale = "es-ES") {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Notas libres con formato (negrita, listas…) vía el editor de Novedades.
 */
export default function MeetingFreeNotesPanel({
  notes = [],
  currentUserId,
  canModerate = false,
  saving = false,
  onCreate,
  onUpdate,
  onDelete,
  dense = false,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [draft, setDraft] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const locale = i18n.language || "es-ES";

  const sorted = useMemo(() => {
    return [...(notes || [])].sort((a, b) => {
      const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return ta - tb || Number(a?.id) - Number(b?.id);
    });
  }, [notes]);

  useEffect(() => {
    if (!dialogOpen) {
      setDraft("");
      setEditingNote(null);
    }
  }, [dialogOpen]);

  const openNew = () => {
    setEditingNote(null);
    setDraft("");
    setDialogOpen(true);
  };

  const openEdit = (note) => {
    setEditingNote(note);
    setDraft(String(note?.body || ""));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const submit = async () => {
    if (saving) return;
    const safe = sanitizeNovedadHtml(draft);
    if (isEmptyNovedadHtml(safe)) return;
    if (editingNote?.id && onUpdate) {
      await onUpdate(editingNote, safe);
    } else {
      await onCreate?.(safe);
    }
    setDialogOpen(false);
    setDraft("");
    setEditingNote(null);
  };

  const plainLen = htmlToPlainText(draft || "").length;

  const closeDeleteDialog = () => {
    if (saving) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || saving) return;
    await onDelete?.(deleteTarget);
    setDeleteTarget(null);
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: alpha(theme.palette.background.paper, 0.9),
        p: dense ? 1.25 : 1.5,
        minWidth: 0,
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
        <NotesOutlinedIcon
          sx={{ mt: 0.15, color: "primary.main", fontSize: 22 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, letterSpacing: 0.02 }}
          >
            {t("Meetings.freeNotesTitle")}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.35, lineHeight: 1.4 }}
          >
            {t("Meetings.freeNotesHint")}
          </Typography>
        </Box>
      </Stack>

      <Button
        fullWidth
        variant="contained"
        size="large"
        startIcon={<AddOutlinedIcon />}
        onClick={openNew}
        disabled={saving}
        sx={{
          mb: 1.25,
          minHeight: 48,
          fontWeight: 700,
          textTransform: "none",
        }}
      >
        {t("Meetings.freeNotesWrite")}
      </Button>

      <Stack
        spacing={1}
        sx={{
          maxHeight: { xs: 320, md: 420 },
          overflowY: "auto",
          pr: 0.25,
          minHeight: sorted.length ? 80 : 48,
        }}
      >
        {sorted.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 1, textAlign: "center" }}
          >
            {t("Meetings.freeNotesEmpty")}
          </Typography>
        ) : (
          sorted.map((note) => {
            const mine = Number(note.userId) === Number(currentUserId);
            const canRemove = mine || canModerate;
            const canEdit = mine && typeof onUpdate === "function";
            return (
              <Box
                key={note.id}
                sx={{
                  p: 1.35,
                  borderRadius: 1.5,
                  bgcolor: mine
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.action.hover, 0.45),
                  border: "1px solid",
                  borderColor: mine
                    ? alpha(theme.palette.primary.main, 0.28)
                    : "divider",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  spacing={0.5}
                  sx={{ minWidth: 0 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, display: "block", mb: 0.5 }}
                    >
                      {note.username || t("Meetings.freeNotesAnonymous")}
                      {note.createdAt
                        ? ` · ${formatNoteTime(note.createdAt, locale)}`
                        : ""}
                    </Typography>
                    <NovedadHtmlBody
                      html={note.body}
                      sx={{ fontSize: "0.875rem", lineHeight: 1.5 }}
                    />
                  </Box>
                  {canEdit ? (
                    <IconButton
                      size="small"
                      aria-label={t("Meetings.freeNotesEdit")}
                      disabled={saving}
                      onClick={() => openEdit(note)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                  {canRemove ? (
                    <IconButton
                      size="small"
                      color="error"
                      aria-label={t("Meetings.freeNotesDelete")}
                      disabled={saving}
                      onClick={() => setDeleteTarget(note)}
                    >
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </Stack>
              </Box>
            );
          })
        )}
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullScreen={isNarrow}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
              {editingNote
                ? t("Meetings.freeNotesEditTitle")
                : t("Meetings.freeNotesWriteTitle")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5, lineHeight: 1.35 }}
            >
              {t("Meetings.freeNotesDialogHint")}
            </Typography>
          </Box>
          <IconButton
            onClick={closeDialog}
            disabled={saving}
            aria-label={t("Meetings.cancel")}
          >
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5 }}>
          <NovedadRichTextEditor
            value={draft}
            onChange={setDraft}
            showLabel={false}
            showHint
            hint={t("Meetings.freeNotesFormatHint")}
            placeholder={t("Meetings.freeNotesPlaceholder")}
            minHeight={isNarrow ? 320 : 280}
            maxHeight={isNarrow ? 520 : 420}
            ariaLabel={t("Meetings.freeNotesWrite")}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            {t("Meetings.freeNotesCharCount", {
              count: plainLen,
              max: 100000,
            })}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: 1,
            "& > :not(style)": { m: 0 },
          }}
        >
          <Button fullWidth={isNarrow} onClick={closeDialog} disabled={saving}>
            {t("Meetings.cancel")}
          </Button>
          <Button
            fullWidth={isNarrow}
            variant="contained"
            onClick={submit}
            disabled={saving || isEmptyNovedadHtml(draft)}
            sx={{ minHeight: 48, fontWeight: 700 }}
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("Meetings.freeNotesSave")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="xs"
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(10px)",
              backgroundColor: "rgba(15, 23, 42, 0.45)",
            },
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        }}
      >
        <DialogTitle>{t("Meetings.freeNotesDeleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Meetings.freeNotesDeleteBody", {
              author:
                deleteTarget?.username || t("Meetings.freeNotesAnonymous"),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={closeDeleteDialog} disabled={saving}>
            {t("Meetings.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={saving}
            onClick={confirmDelete}
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlineOutlinedIcon />
              )
            }
          >
            {t("Meetings.freeNotesDeleteConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
